import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useReferralCodeValidation } from '@/hooks/useReferralCodeValidation';
import { UserTypeSelection } from './UserTypeSelection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { PasswordStrength } from '@/components/ui/password-strength';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';

const TURNSTILE_SITE_KEY =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ||
  '0x4AAAAAADu5E_3EbOZSjywt'; // Cloudflare Turnstile production site key (public)

type AuthStep = 'type-selection' | 'sign-up-form' | 'sign-in-form';

export const MultiStepAuth = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const prefillEmail = searchParams.get('email');
  const companyName = searchParams.get('company');
  const role = searchParams.get('role');
  const isSignUp = searchParams.get('signup') === 'true' || mode === 'signup';
  const isTeamInvitation = companyName && role;
  
  // Initialize step based on URL parameters
  const getInitialStep = (): AuthStep => {
    if (isSignUp) {
      // For team invitations, skip type selection and go directly to signup form
      if (isTeamInvitation) {
        return 'sign-up-form';
      }
      return 'type-selection';
    }
    return 'sign-in-form';
  };
  
  const [currentStep, setCurrentStep] = useState<AuthStep>(getInitialStep());
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [signUpCaptchaToken, setSignUpCaptchaToken] = useState<string | null>(null);
  const [signInCaptchaToken, setSignInCaptchaToken] = useState<string | null>(null);
  const signUpCaptchaRef = useRef<TurnstileInstance>(null);
  const signInCaptchaRef = useRef<TurnstileInstance>(null);
  const { signUp, signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Watch for URL parameter changes and update the current step accordingly
  useEffect(() => {
    const isSignUpParam = searchParams.get('signup') === 'true' || searchParams.get('mode') === 'signup';
    if (isSignUpParam && currentStep !== 'type-selection' && currentStep !== 'sign-up-form') {
      setCurrentStep('type-selection');
    } else if (!isSignUpParam && (currentStep === 'type-selection' || currentStep === 'sign-up-form')) {
      setCurrentStep('sign-in-form');
    }
  }, [searchParams]);

  // Prefill email from URL parameters
  useEffect(() => {
    if (prefillEmail) {
      setSignUpData(prev => ({ ...prev, email: decodeURIComponent(prefillEmail) }));
      setSignInData(prev => ({ ...prev, email: decodeURIComponent(prefillEmail) }));
    }
  }, [prefillEmail]);

  const [signUpData, setSignUpData] = useState({
    email: '',
    password: '',
    fullName: '',
    userType: (isTeamInvitation ? 'employer' : 'job_seeker') as 'job_seeker' | 'employer',
    referralCode: '',
    noticePeriod: ''
  });
  
  // Add referral code validation hook for sign-up
  const { status: referralStatus, message: referralMessage, validateReferralCode } = useReferralCodeValidation();
  

  const [signInData, setSignInData] = useState({
    email: '',
    password: ''
  });

  const handleUserTypeSelect = (userType: 'job_seeker' | 'employer') => {
    setSignUpData({ ...signUpData, userType });
    setCurrentStep('sign-up-form');
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate captcha
    if (!signUpCaptchaToken) {
      toast.error('Please complete the captcha verification');
      return;
    }
    
    // Validate password strength
    const passwordRequirements = [
      signUpData.password.length >= 8,
      /[A-Z]/.test(signUpData.password),
      /[a-z]/.test(signUpData.password),
      /\d/.test(signUpData.password),
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(signUpData.password)
    ];
    
    const passedRequirements = passwordRequirements.filter(Boolean).length;
    if (passedRequirements < 3) {
      toast.error('Please create a stronger password. Your password should meet at least 3 of the requirements.');
      return;
    }
    
    // Validate notice period for job seekers
    if (signUpData.userType === 'job_seeker' && !signUpData.noticePeriod) {
      toast.error('Please select your notice period');
      return;
    }
    
    setIsLoading(true);

    try {
      const { error } = await signUp(
        signUpData.email,
        signUpData.password,
        signUpData.fullName,
        signUpData.userType,
        signUpData.referralCode || undefined,
        signUpData.userType === 'job_seeker' ? signUpData.noticePeriod : undefined,
        signUpCaptchaToken
      );

      if (error) {
        console.error('Edge function error details:', { error, type: typeof error, message: error?.message });
        const errorMessage = typeof error === 'string' ? error : 
                           error.message || 
                           'Failed to create account. Please try again.';
        console.error('Parsed error message:', errorMessage);
        toast.error(errorMessage);
        // Reset captcha on error
        signUpCaptchaRef.current?.reset();
        setSignUpCaptchaToken(null);
      } else {
        toast.success('A confirmation email has been sent to your inbox. Please click the link to activate your account.');
        
        // Check if this is for a team invitation
        const storedInvitation = localStorage.getItem('pendingTeamInvitation');
        if (storedInvitation) {
          navigate('/team-invitation/process');
        } else {
          // Regular onboarding flow
          if (signUpData.userType === 'job_seeker') {
            navigate(`/onboarding/job-seeker?email=${encodeURIComponent(signUpData.email)}`);
          } else {
            navigate(`/onboarding/employer?email=${encodeURIComponent(signUpData.email)}`);
          }
        }
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
      // Reset captcha on error
      signUpCaptchaRef.current?.reset();
      setSignUpCaptchaToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate captcha
    if (!signInCaptchaToken) {
      toast.error('Please complete the captcha verification');
      return;
    }
    
    setIsLoading(true);

    try {
      const { error } = await signIn(signInData.email, signInData.password, signInCaptchaToken);

      if (error) {
        if (error.message.includes('Email not confirmed')) {
          toast.error('Please verify your email address before signing in. Check your inbox for the verification link.');
        } else {
          toast.error(error.message);
        }
        // Reset captcha on error
        signInCaptchaRef.current?.reset();
        setSignInCaptchaToken(null);
      } else {
        toast.success('Signed in successfully!');
        
        // Check if this is for a team invitation
        const storedInvitation = localStorage.getItem('pendingTeamInvitation');
        if (storedInvitation) {
          navigate('/team-invitation/process');
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
      // Reset captcha on error
      signInCaptchaRef.current?.reset();
      setSignInCaptchaToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        toast.error(error.message);
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  if (currentStep === 'type-selection') {
    return (
      <UserTypeSelection
        onSelect={handleUserTypeSelect}
      />
    );
  }

  if (currentStep === 'sign-up-form') {
    return (
      <Card className="shadow-xl">
        <CardHeader>
          {/* Team Invitation Banner */}
          {isTeamInvitation && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2 text-blue-800">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
                </svg>
                <div>
                  <p className="font-medium">Team Invitation</p>
                  <p className="text-sm">Join {companyName} as {role}</p>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {isTeamInvitation 
                  ? `Complete your signup to join ${companyName}`
                  : `Create your ${signUpData.userType === 'job_seeker' ? 'Job Seeker' : 'Employer'} account`
                }
              </CardTitle>
              <CardDescription>
                {isTeamInvitation 
                  ? `You've been invited to join as ${role}`
                  : "Join thousands of professionals in Sri Lanka"
                }
              </CardDescription>
            </div>
            {!isTeamInvitation && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentStep('type-selection')}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignUp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullname">Full Name</Label>
              <Input
                id="fullname"
                type="text"
                placeholder="Enter your full name"
                value={signUpData.fullName}
                onChange={(e) => setSignUpData({ ...signUpData, fullName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={signUpData.email}
                onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                value={signUpData.password}
                onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                required
              />
              <PasswordStrength password={signUpData.password} />
            </div>
            
            {/* Notice Period - Only show for job seekers */}
            {signUpData.userType === 'job_seeker' && (
              <div className="space-y-2">
                <Label htmlFor="noticePeriod">Notice Period *</Label>
                <Select 
                  value={signUpData.noticePeriod} 
                  onValueChange={(value) => setSignUpData({ ...signUpData, noticePeriod: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your notice period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immediate</SelectItem>
                    <SelectItem value="1_week">1 Week</SelectItem>
                    <SelectItem value="2_weeks">2 Weeks</SelectItem>
                    <SelectItem value="1_month">1 Month</SelectItem>
                    <SelectItem value="2_months">2 Months</SelectItem>
                    <SelectItem value="3_months">3 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="referralCode">Referral Code (Optional)</Label>
              <Input
                id="referralCode"
                type="text"
                placeholder="Enter referral code if you have one"
                value={signUpData.referralCode}
                onChange={(e) => {
                  const code = e.target.value.toUpperCase();
                  setSignUpData({ ...signUpData, referralCode: code });
                  // Validate referral code in real-time with debouncing
                  validateReferralCode(code);
                }}
                className={
                  referralStatus === 'valid' ? 'border-green-500' :
                  referralStatus === 'invalid' ? 'border-red-500' : ''
                }
              />
              {referralStatus === 'checking' && (
                <p className="text-xs text-blue-600 flex items-center">
                  <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Checking referral code...
                </p>
              )}
              {referralStatus === 'valid' && (
                <p className="text-xs text-green-600 flex items-center">
                  <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  {referralMessage}
                </p>
              )}
              {referralStatus === 'invalid' && referralMessage && (
                <p className="text-xs text-red-600 flex items-center">
                  <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                  {referralMessage}
                </p>
              )}
              {referralStatus === 'idle' && (
                <p className="text-xs text-muted-foreground">
                  Have a referral code? Enter it to unlock special rewards!
                </p>
              )}
            </div>
            
            {/* Cloudflare Turnstile for Sign Up */}
            <div className="flex justify-center">
              <Turnstile
                ref={signUpCaptchaRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setSignUpCaptchaToken(token)}
                onExpire={() => setSignUpCaptchaToken(null)}
                onError={() => setSignUpCaptchaToken(null)}
                options={{ theme: 'light' }}
              />
            </div>
            
            <Button type="submit" className="w-full" disabled={isLoading || isGoogleLoading || !signUpCaptchaToken}>
              {isLoading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
          
          <div className="mt-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>
            
            <Button 
              type="button" 
              variant="outline" 
              className="w-full mt-4" 
              onClick={handleGoogleSignIn}
              disabled={isLoading || isGoogleLoading}
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {isGoogleLoading ? 'Connecting...' : 'Continue with Google'}
            </Button>
          </div>

          <div className="mt-4 text-center">
           <Button 
             variant="link" 
             className="text-sm" 
             onClick={() => {
               // Update URL to reflect the change - let useEffect handle state
               navigate('/auth');
             }}
            >
              Already have an account? Sign in
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sign-in form
  return (
    <Card className="shadow-xl">
      <CardHeader>
        <CardTitle>Welcome back</CardTitle>
        <CardDescription>
          Sign in to your account to continue
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSignIn} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="signin-email">Email</Label>
            <Input
              id="signin-email"
              type="email"
              placeholder="Enter your email"
              value={signInData.email}
              onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="signin-password">Password</Label>
            <Input
              id="signin-password"
              type="password"
              placeholder="Enter your password"
              value={signInData.password}
              onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
              required
            />
          </div>
          
          {/* Cloudflare Turnstile for Sign In */}
          <div className="flex justify-center">
            <Turnstile
              ref={signInCaptchaRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={(token) => setSignInCaptchaToken(token)}
              onExpire={() => setSignInCaptchaToken(null)}
              onError={() => setSignInCaptchaToken(null)}
              options={{ theme: 'light' }}
            />
          </div>
          
          <Button type="submit" className="w-full" disabled={isLoading || isGoogleLoading || !signInCaptchaToken}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
        
        <div className="mt-4">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or continue with
              </span>
            </div>
          </div>
          
          <Button 
            type="button" 
            variant="outline" 
            className="w-full mt-4" 
            onClick={handleGoogleSignIn}
            disabled={isLoading || isGoogleLoading}
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {isGoogleLoading ? 'Connecting...' : 'Continue with Google'}
          </Button>
        </div>

        <div className="mt-4 text-center">
           <Button 
             variant="link" 
             className="text-sm" 
             onClick={() => {
               // Update URL to reflect the change - let useEffect handle state
               navigate('/auth?signup=true');
             }}
          >
            Don't have an account? Sign up
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};